import { nonEmptyString } from '../lib/format';
import { errorMessage } from '../lib/errors';
import { logger } from '../lib/logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  MatterResponse, NegotiationState, ProofGraph, ClioMatter, Thread, CalendarEvent, ClioActivity, ClioContact, ClioDocument, ClioTask, DamageItem, Preferences, Template,
} from '../types';
import { apiFetch } from '../lib/api';
import { getBillingState, hasPremiumAccess } from '../lib/billing';
import { migrateProofGraph } from '../lib/proofGraph';
import type { LoadingStates } from '../../components/DbLoadingGraph';
import type { TabId } from '../components/layout/AppSidebar';

export interface UseMatterDataOptions {
  activeTab: TabId;
  onPreferencesLoaded: (prefs: Preferences) => void;
}

const INITIAL_LOADING_STATES: LoadingStates = {
  gateway: 'completed',
  system: 'pending',
  preferences: 'pending',
  templates: 'pending',
  matter: 'pending',
  clioSync: 'pending',
  tasks: 'pending',
  proofGraph: 'pending',
  directory: 'pending',
  contacts: 'pending',
};

const INITIAL_MATTER_LIMIT = 25;

function mattersPath(includeAll: boolean, limit?: number): string {
  const params = new URLSearchParams();
  if (includeAll) params.set('include_all', 'true');
  if (limit) params.set('limit', String(limit));
  const query = params.toString();
  return `/api/v1/matters${query ? `?${query}` : ''}`;
}

function chooseDefaultMatter(available: ClioMatter[]): ClioMatter | null {
  return available.find((matter) => String(matter.id) === '1001') ?? available[0] ?? null;
}

// Owns every piece of App.tsx's matter/global data-fetching: auth check,
// matter-id resolution from the URL, the one-time global fetch (contacts/
// templates/preferences), the per-matter polling fetch, and the three
// lazy-load effects (documents/threads/damages), plus the state they
// populate. This is the highest-risk extraction in the refactor — the
// effects' dependency arrays and the lazy-load guard refs are preserved
// exactly as they were in App.tsx.
export function useMatterData({ activeTab, onPreferencesLoaded }: UseMatterDataOptions): UseMatterDataResult {
  const [matterId, setMatterId] = useState<string>('');
  const [mattersList, setMattersList] = useState<ClioMatter[]>([]);
  const [showAllMatters, setShowAllMatters] = useState(false);
  const [mattersListLoading, setMattersListLoading] = useState(false);
  const [moreMattersLoading, setMoreMattersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbLoadingStates, setDbLoadingStates] = useState<LoadingStates>(INITIAL_LOADING_STATES);
  const [error, setError] = useState<string | null>(null);

  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [initializationCheckedMatterId, setInitializationCheckedMatterId] = useState<string>('');
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);

  const [clioMatter, setClioMatter] = useState<ClioMatter | null>(null);
  const [clioTasks, setClioTasks] = useState<ClioTask[]>([]);
  const [clioDocuments, setClioDocuments] = useState<ClioDocument[]>([]);
  const [clioActivities, setClioActivities] = useState<ClioActivity[]>([]);
  const [clioCalendarEvents, setClioCalendarEvents] = useState<CalendarEvent[]>([]);
  const [proofGraph, setProofGraph] = useState<ProofGraph | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [damages, setDamages] = useState<DamageItem[]>([]);
  const [contacts, setContacts] = useState<ClioContact[]>([]);
  const [accountUsers, setAccountUsers] = useState<ClioContact[]>([]);
  const [matterContacts, setMatterContacts] = useState<ClioContact[]>([]);

  const [matterScope, setMatterScope] = useState({ authenticated, matterId });
  if (matterScope.authenticated !== authenticated || matterScope.matterId !== matterId) {
    setMatterScope({ authenticated, matterId });
    setMatterContacts([]);
    setInitializationCheckedMatterId('');
    if (authenticated === false) setLoading(false);
    if (authenticated === true && !matterId) setShowWizard(true);
  }

  // Tracks whether the one-time global fetches (preferences/templates/contacts)
  // have completed, so per-matter loads don't re-run or re-animate them.
  const globalsReadyRef = useRef(false);
  // Guards against re-fetching documents/threads on every tab switch; each
  // holds the matterId they were last lazily loaded for.
  const documentsLoadedMatterIdRef = useRef<string | null>(null);
  const threadsLoadedMatterIdRef = useRef<string | null>(null);
  const damagesLoadedMatterIdRef = useRef<string | null>(null);
  const lastMatterIdRef = useRef('');
  const lastMatterStorageKeyRef = useRef('');
  const preferredMatterIdRef = useRef('');
  const failedPreferredMatterIdRef = useRef('');
  const mattersListRef = useRef<ClioMatter[]>([]);
  const mattersLoadGenerationRef = useRef(0);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const res = await apiFetch(`/api/v1/auth/status`);
        if (res.ok) {
          const data = await res.json<{ authenticated: boolean; user_id?: string; last_matter_id?: string; initialized?: boolean }>();
          if (data.authenticated) {
            const storageKey = data.user_id ? `lattice:last-matter:${data.user_id}` : 'lattice:last-matter';
            lastMatterStorageKeyRef.current = storageKey;
            lastMatterIdRef.current = String(((nonEmptyString(data.last_matter_id) ?? localStorage.getItem(storageKey)) ?? ''));
          }
          if (data.authenticated) {
            const targetId = new URLSearchParams(window.location.search).get('matter_id') ?? lastMatterIdRef.current;
      if (targetId) {
        setMatterId(targetId);
        setClioMatter({ id: targetId, display_number: '', description: '', client: '', practice_area: '', open_date: '', status: '' });
        setLoading(false);

        const url = new URL(window.location.href);
        if (url.searchParams.get('matter_id') !== targetId) {
          url.searchParams.set('matter_id', targetId);
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        setLoading(true);
      }
          }
          setAuthenticated(data.authenticated);
        } else {
          setAuthenticated(false);
        }
      } catch {
        setAuthenticated(false);
      }
    };
    void checkAuth();
  }, []);

  // Premium UI state mirrors the server-side plan. The extraction endpoints
  // enforce access independently, so browser storage is never an entitlement.
  useEffect(() => {
    if (authenticated !== true) return;
    let cancelled = false;
    void getBillingState()
      .then((state): void => { if (!cancelled) setIsPremiumUnlocked(hasPremiumAccess(state)); })
      .catch((): void => { if (!cancelled) setIsPremiumUnlocked(false); });
    return (): void => { cancelled = true; };
  }, [authenticated]);

  const loadMattersProgressively = useCallback(async (includeAll: boolean): Promise<ClioMatter[]> => {
    const generation = ++mattersLoadGenerationRef.current;
    setMattersListLoading(true);
    setMoreMattersLoading(false);

    const initialRes = await apiFetch(mattersPath(includeAll, INITIAL_MATTER_LIMIT));
    if (generation !== mattersLoadGenerationRef.current) return [];
    if (initialRes.status === 401) {
      setAuthenticated(false);
      setMattersListLoading(false);
      return [];
    }
    if (!initialRes.ok) {
      setMattersListLoading(false);
      throw new Error('Failed to load matters');
    }

    const initial = ((await initialRes.json<ClioMatter[]>()) ?? []);
    mattersListRef.current = initial;
    setMattersList(initial);
    setMattersListLoading(false);

    if (initial.length < INITIAL_MATTER_LIMIT) {
      return initial;
    }

    setMoreMattersLoading(true);
    void (async (): Promise<void> => {
      try {
        const fullRes = await apiFetch(mattersPath(includeAll));
        if (generation !== mattersLoadGenerationRef.current) return;
        if (fullRes.status === 401) {
          setAuthenticated(false);
          return;
        }
        if (fullRes.ok) {
          const full = ((await fullRes.json<ClioMatter[]>()) ?? []);
          mattersListRef.current = full;
          setMattersList(full);
        }
      } catch (err) {
        logger.error('Failed to load the remaining matters:', err);
      } finally {
        if (generation === mattersLoadGenerationRef.current) {
          setMoreMattersLoading(false);
        }
      }
    })();
    return initial;
  }, []);

  // Restore the URL matter or the user's last accessed matter immediately.
  // The first 25 matter summaries load independently; the balance replaces
  // that list in the background without blocking the selected matter.
  useEffect(() => {
    const isAuth = authenticated === true;
    if (authenticated === null) {
      return;
    }
    if (!isAuth) {
      return;
    }

    const initMatterAndFetch = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);
      const targetId = String(((nonEmptyString(params.get('matter_id')) ?? lastMatterIdRef.current) ?? ''));
      preferredMatterIdRef.current = targetId;



      try {
        const available = await loadMattersProgressively(false);
        if (!targetId) {
          const fallback = chooseDefaultMatter(available);
          if (fallback) {
            setMatterId(String(fallback.id));
            setShowWizard(!fallback.initialized);
          } else {
            setShowWizard(true);
          }
        } else if (failedPreferredMatterIdRef.current === targetId) {
          const fallback = chooseDefaultMatter(available);
          if (fallback && String(fallback.id) !== targetId) {
            setMatterId(String(fallback.id));
          }
        }
      } catch (err) {
        logger.error('Failed to initialize matters list:', err);
        if (!targetId) setShowWizard(true);
      } finally {
        if (!targetId) setLoading(false);
      }
    };

    void initMatterAndFetch();
  }, [authenticated, loadMattersProgressively]);

  const changeMatterScope = async (includeAll: boolean): Promise<void> => {
    setShowAllMatters(includeAll);
    try {
      await loadMattersProgressively(includeAll);
    } catch (err) {
      logger.error('Failed to change matter list scope:', err);
    }
  };

  // Re-check initialization whenever the active case changes, even when the
  // current case has the onboarding wizard open. The normal matter-detail
  // loader skips work while showWizard is true, so without this independent
  // check switching from an uninitialized case to an initialized one leaves
  // the UI stuck on the old wizard.
  useEffect(() => {
    if (authenticated !== true) return;
    if (!matterId) return;

    let cancelled = false;

    const checkActiveMatter = async (): Promise<void> => {
      try {
        const res = await apiFetch(`/api/v1/matters/${matterId}/initialized`);
        if (cancelled) return;
        if (!res.ok) {
          if (matterId === preferredMatterIdRef.current) {
            failedPreferredMatterIdRef.current = matterId;
            const fallback = chooseDefaultMatter(mattersListRef.current);
            if (fallback && String(fallback.id) !== matterId) {
              setMatterId(String(fallback.id));
              return;
            }
          }
          setShowWizard(true);
          setInitializationCheckedMatterId(matterId);
          return;
        }
        const data = await res.json<{ initialized: boolean }>();
        if (cancelled) return;
        setShowWizard(!data.initialized);
        setInitializationCheckedMatterId(matterId);
      } catch (err) {
        if (cancelled) return;
        logger.error('Active matter initialization check failed:', err);
        if (matterId === preferredMatterIdRef.current) {
          failedPreferredMatterIdRef.current = matterId;
          const fallback = chooseDefaultMatter(mattersListRef.current);
          if (fallback && String(fallback.id) !== matterId) {
            setMatterId(String(fallback.id));
            return;
          }
        }
        setShowWizard(true);
        setInitializationCheckedMatterId(matterId);
      }
    };

    void checkActiveMatter();
    return (): void => { cancelled = true; };
  }, [matterId, authenticated]);

  useEffect(() => {
    if (authenticated !== true || !matterId || !lastMatterStorageKeyRef.current) return;
    localStorage.setItem(lastMatterStorageKeyRef.current, matterId);
  }, [authenticated, matterId]);

  // Matter contacts are needed by the onboarding wizard as well as the main
  // matter views. Keep this fetch independent from the initialized-matter
  // loader below, which intentionally does not run while the wizard is open.
  // Clear first so contacts from the previous matter cannot remain selectable
  // while the new shortlist is loading.
  useEffect(() => {
    if (authenticated !== true || !matterId) return;

    let cancelled = false;
    void apiFetch(`/api/v1/matters/${matterId}/contacts`)
      .then(async (res) => (res.ok ? res.json<ClioContact[]>() : []))
      .then((data) => {
        if (!cancelled) setMatterContacts((data ?? []));
      })
      .catch((err) => {
        if (!cancelled) logger.error('Failed to load matter contacts:', err);
      });

    return (): void => { cancelled = true; };
  }, [authenticated, matterId]);

  // Fetch global assets (contacts, templates, preferences) immediately on authentication (needed for wizard)
  useEffect(() => {
    const isAuth = authenticated === true;
    if (!isAuth) {
      return;
    }

    const fetchGlobals = async (): Promise<void> => {
      try {
        // Fetch contacts
        const contactsRes = await apiFetch(`/api/v1/contacts`);
        if (contactsRes.status === 401) {
          setAuthenticated(false);
          return;
        }
        if (contactsRes.ok) {
          const contactsData = await contactsRes.json<ClioContact[]>();
          setContacts((contactsData ?? []));
          setDbLoadingStates(prev => ({ ...prev, contacts: 'completed', directory: 'completed' }));
        } else {
          setDbLoadingStates(prev => ({ ...prev, contacts: 'failed', directory: 'failed' }));
        }

        // Fetch contact-shaped records for users in the authenticated Clio
        // account. Attorney-only settings must not use the firm-wide contacts
        // directory; the API also supplies user-backed records when Clio omits
        // an internal user's optional linked contact.
        const accountUsersRes = await apiFetch(`/api/v1/account/users`);
        if (accountUsersRes.status === 401) {
          setAuthenticated(false);
          return;
        }
        if (accountUsersRes.ok) {
          const accountUsersData = await accountUsersRes.json<ClioContact[]>();
          setAccountUsers((accountUsersData ?? []));
        }

        // Fetch templates
        const tmplRes = await apiFetch(`/api/v1/config/templates`);
        if (tmplRes.status === 401) {
          setAuthenticated(false);
          return;
        }
        if (tmplRes.ok) {
          const tmplData = await tmplRes.json<Template[]>();
          setTemplates(tmplData);
          setDbLoadingStates(prev => {
            const next = { ...prev, templates: 'completed' as const };
            if (next.preferences === 'completed') {
              next.system = 'completed';
            }
            return next;
          });
        } else {
          setDbLoadingStates(prev => ({ ...prev, templates: 'failed', system: 'failed' }));
        }

        // Fetch preferences
        const prefRes = await apiFetch(`/api/v1/config/preferences`);
        if (prefRes.status === 401) {
          setAuthenticated(false);
          return;
        }
        if (prefRes.ok) {
          const prefData = await prefRes.json<Preferences>();
          onPreferencesLoaded(prefData);
          setDbLoadingStates(prev => {
            const next = { ...prev, preferences: 'completed' as const };
            if (next.templates === 'completed') {
              next.system = 'completed';
            }
            return next;
          });
        } else {
          setDbLoadingStates(prev => ({ ...prev, preferences: 'failed', system: 'failed' }));
        }
      } catch (err) {
        logger.error('Failed to fetch authenticated globals:', err);
      } finally {
        globalsReadyRef.current = true;
      }
    };

    void fetchGlobals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  // Fetch matter details, preferences, templates, and contacts with periodic polling
  useEffect(() => {
    const isAuth = authenticated === true;
    if (!isAuth || showWizard) {
      return;
    }
    if (!matterId) {
      return;
    }
    if (initializationCheckedMatterId !== matterId) {
      return;
    }

    const fetchData = async (): Promise<void> => {
      setDbLoading(true);
      // Preferences/templates/contacts are firm-wide, not matter-scoped, and
      // are already fetched once by fetchGlobals — don't re-fetch or
      // re-animate them on every matter switch.
      const globalsReady = globalsReadyRef.current;
      setDbLoadingStates({
        gateway: 'completed',
        system: globalsReady ? 'completed' : 'loading',
        preferences: globalsReady ? 'completed' : 'loading',
        templates: globalsReady ? 'completed' : 'loading',
        matter: 'loading',
        clioSync: 'loading',
        tasks: 'pending',
        proofGraph: 'pending',
        directory: globalsReady ? 'completed' : 'loading',
        contacts: globalsReady ? 'completed' : 'loading',
      });

      try {
        const promises = [];

        // Matter details & Clio tasks (documents and threads are lazy-loaded
        // separately, only when a tab that shows them is opened)
        promises.push((async (): Promise<void> => {
          try {
            const res = await apiFetch(`/api/v1/matters/${matterId}`);
            if (res.status === 401) {
              setAuthenticated(false);
              return;
            }
            if (!res.ok) {
              throw new Error('Failed to load matter detail');
            }
            const matterData = await res.json<MatterResponse>();
            setClioMatter(matterData.clio);
            setDbLoadingStates(prev => ({ ...prev, clioSync: 'completed' }));

            setClioTasks((matterData.tasks ?? []));
            setClioActivities((matterData.activities ?? []));
            setClioCalendarEvents((matterData.calendar_events ?? []));
            setDbLoadingStates(prev => ({ ...prev, tasks: 'completed' }));

            setProofGraph(migrateProofGraph(matterData.graph));
            setDbLoadingStates(prev => ({ ...prev, proofGraph: 'completed', matter: 'completed' }));

            if (!matterData.graph?.template_id) {
              setShowWizard(true);
            } else {
              setShowWizard(false);
            }
          } catch (err) {
            setDbLoadingStates(prev => ({
              ...prev,
              clioSync: 'failed',
              tasks: 'failed',
              proofGraph: 'failed',
              matter: 'failed',
            }));
            throw err;
          }
        })());

        await Promise.all(promises);
        setDbLoading(false);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? errorMessage(err) : 'An error occurred while loading data.');
        setDbLoading(false);
      }
    };

    void fetchData();
  }, [matterId, authenticated, showWizard, initializationCheckedMatterId]);

  // Lazy-load documents only when a tab that actually displays them is opened
  // (mapper/stageboard/negotiation), instead of bundling them into every
  // matter load. Fetches once per matterId.
  useEffect(() => {
    const needsDocuments = activeTab === 'mapper' || activeTab === 'dashboard' || activeTab === 'negotiation' || activeTab === 'experts';
    if (!needsDocuments || !matterId) {
      return;
    }
    if (documentsLoadedMatterIdRef.current === matterId) {
      return;
    }
    documentsLoadedMatterIdRef.current = matterId;

    apiFetch(`/api/v1/matters/${matterId}/documents`)
      .then(res => (res.ok ? res.json<ClioDocument[]>() : []))
      .then(data => setClioDocuments((data ?? [])))
      .catch(err => logger.error('Failed to lazy-load documents:', err));
  }, [matterId, activeTab]);

  // Lazy-load conversation threads (for the per-claim unread badge in
  // ClaimCard) only once the claim views (mapper/dashboard) are opened.
  // Waits for the proof graph so claims exist to merge threads into.
  useEffect(() => {
    const needsThreads = activeTab === 'mapper' || activeTab === 'dashboard';
    if (!needsThreads || !matterId || !proofGraph) {
      return;
    }
    if (threadsLoadedMatterIdRef.current === matterId) {
      return;
    }
    threadsLoadedMatterIdRef.current = matterId;

    apiFetch(`/api/v1/matters/${matterId}/threads`)
      .then(res => (res.ok ? res.json<Record<string, Thread>>() : null))
      .then((threadMap: Record<string, Thread> | null) => {
        if (!threadMap) return;
        setProofGraph((prev) => {
          if (!prev || !Array.isArray(prev.claims)) return prev;
          return {
            ...prev,
            claims: prev.claims.map((c) => ({
              ...c,
              thread: threadMap[c.id] ?? c.thread ?? { messages: [] },
            })),
          };
        });
      })
      .catch(err => logger.warn('Failed to lazy-load threads:', err));
  }, [matterId, activeTab, proofGraph]);

  // Lazy-load damages (from negotiation data) only once the Proof Graph or
  // Negotiation tab is opened, so each ClaimCard can show damages linked to
  // it. Negotiation tab manages its own copy independently and refetches on
  // every mount, so it always reflects damages added here.
  useEffect(() => {
    const needsDamages = activeTab === 'mapper' || activeTab === 'negotiation';
    if (!needsDamages || !matterId) {
      return;
    }
    if (damagesLoadedMatterIdRef.current === matterId) {
      return;
    }
    damagesLoadedMatterIdRef.current = matterId;

    apiFetch(`/api/v1/matters/${matterId}/negotiation`)
      .then(res => (res.ok ? res.json<NegotiationState>() : null))
      .then(data => setDamages((data?.damages ?? [])))
      .catch(err => logger.error('Failed to lazy-load damages:', err));
  }, [matterId, activeTab]);

  return {
    matterId, setMatterId, mattersList, showAllMatters, mattersListLoading, moreMattersLoading, changeMatterScope,
    loading, dbLoading, dbLoadingStates, error, setError,
    authenticated, setAuthenticated, showWizard, setShowWizard,
    isPremiumUnlocked, setIsPremiumUnlocked,
    clioMatter, clioTasks, setClioTasks, clioDocuments,
    clioActivities, setClioActivities, clioCalendarEvents, setClioCalendarEvents,
    proofGraph, setProofGraph,
    templates, setTemplates,
    damages, setDamages,
    contacts, setContacts, accountUsers, matterContacts, setMatterContacts,
  };
}

export interface UseMatterDataResult {
    matterId: string;
    setMatterId: import("react").Dispatch<import("react").SetStateAction<string>>;
    mattersList: ClioMatter[];
    showAllMatters: boolean;
    mattersListLoading: boolean;
    moreMattersLoading: boolean;
    changeMatterScope: (includeAll: boolean) => Promise<void>;
    loading: boolean;
    dbLoading: boolean;
    dbLoadingStates: LoadingStates;
    error: string | null;
    setError: import("react").Dispatch<import("react").SetStateAction<string | null>>;
    authenticated: boolean | null;
    setAuthenticated: import("react").Dispatch<import("react").SetStateAction<boolean | null>>;
    showWizard: boolean;
    setShowWizard: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    isPremiumUnlocked: boolean;
    setIsPremiumUnlocked: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    clioMatter: ClioMatter | null;
    clioTasks: ClioTask[];
    setClioTasks: import("react").Dispatch<import("react").SetStateAction<ClioTask[]>>;
    clioDocuments: ClioDocument[];
    clioActivities: ClioActivity[];
    setClioActivities: import("react").Dispatch<import("react").SetStateAction<ClioActivity[]>>;
    clioCalendarEvents: CalendarEvent[];
    setClioCalendarEvents: import("react").Dispatch<import("react").SetStateAction<CalendarEvent[]>>;
    proofGraph: ProofGraph | null;
    setProofGraph: import("react").Dispatch<import("react").SetStateAction<ProofGraph | null>>;
    templates: Template[];
    setTemplates: import("react").Dispatch<import("react").SetStateAction<Template[]>>;
    damages: DamageItem[];
    setDamages: import("react").Dispatch<import("react").SetStateAction<DamageItem[]>>;
    contacts: ClioContact[];
    setContacts: import("react").Dispatch<import("react").SetStateAction<ClioContact[]>>;
    accountUsers: ClioContact[];
    matterContacts: ClioContact[];
    setMatterContacts: import("react").Dispatch<import("react").SetStateAction<ClioContact[]>>;
}

